// ============================================================================
// BESPOKIAN Health - Public Landing Page
// Luxury pharmacy homepage with transparent pricing
// ============================================================================

import { Link } from '@tanstack/react-router';
import { ArrowRight, CheckCircle2, Shield, Clock, Award, TrendingDown, Pill, Phone, Video, MessageCircle } from 'lucide-react';
import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { StepCards, Step } from '@/components/common/StepCards';

const popularDrugs = [
  { name: 'Lisinopril 10mg', ourPrice: '$3.45', retailPrice: '$18.00', savings: '81%' },
  { name: 'Metformin 500mg', ourPrice: '$4.25', retailPrice: '$22.00', savings: '81%' },
  { name: 'Atorvastatin 20mg', ourPrice: '$7.85', retailPrice: '$35.00', savings: '78%' },
  { name: 'Omeprazole 20mg', ourPrice: '$6.95', retailPrice: '$28.00', savings: '75%' },
];

const stats = [
  { value: 'Up to 80%', label: 'Potential Savings', icon: TrendingDown },
  { value: '50', label: 'States (Licensing)', icon: Pill, comingSoon: true },
  { value: 'Licensed', label: 'Pharmacist Support', icon: Phone, comingSoon: true },
  { value: 'HIPAA', label: 'Compliant', icon: Shield, comingSoon: true },
];

const howItWorksSteps: Step[] = [
  { 
    title: 'Transfer or Send Rx', 
    description: "Have your doctor send us a prescription, or we'll transfer one from your current pharmacy.",
    badgeColor: 'bg-gold-500'
  },
  { 
    title: 'See Exact Price', 
    description: 'We show you the full breakdown — cost + 15% + $5 fee. Compare insurance vs cash.',
    badgeColor: 'bg-sage-500'
  },
  { 
    title: 'Get Fast Delivery', 
    description: 'Standard shipping typically 2-3 days. We plan to offer free shipping on orders over $50.',
    badgeColor: 'bg-navy-700'
  },
];

export function LandingPage() {
  const scrollToNextSection = () => {
    const nextSection = document.getElementById('pricing-hero');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />

      {/* New Hero Section with Chemistry Lab Background */}
      <section
        className="relative h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 21, 58, 0.55), rgba(0, 21, 58, 0.9)), url("/images/chemistry-lab-hero.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center center'
        }}
      >
        <div className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center">
          <div className="hidden md:block w-24 h-0.5 bg-white mx-auto mb-8" />
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-white mb-6 md:mb-8 leading-tight tracking-tight">
            Pay What Drugs Actually Cost
          </h1>
          <div className="hidden md:block w-24 h-0.5 bg-white mx-auto mb-12" />

          {/* Learn More - Scroll to pricing section */}
          <div className="max-w-xl mx-auto">
            <div
              id="learn-more"
              role="button"
              tabIndex={0}
              onClick={scrollToNextSection}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') scrollToNextSection();
              }}
              className="bg-white/10 backdrop-blur-md rounded-lg px-6 py-4 border border-white/20 shadow-xl ring-1 ring-white/10 cursor-pointer hover:bg-white/20 transition-all duration-300 w-full"
              aria-label="Learn more about pricing"
            >
              <div className="flex items-center justify-between gap-6">
                <div className="text-left">
                  <div className="text-white/90 text-xs uppercase tracking-widest mb-2">
                    LEARN MORE
                  </div>
                  <div className="text-white/70 text-lg">
                    See how it works
                  </div>
                </div>
                <MessageCircle className="h-6 w-6 text-white/80 flex-shrink-0" aria-hidden="true" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Original Hero Section - Now Second Section */}
      <section id="pricing-hero" className="relative overflow-hidden pt-20 bg-gradient-to-br from-navy-50 via-white to-gold-50/30">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold-200/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-sage-200/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Copy */}
            <div>
              {/* Gold accent line */}
              <div className="w-16 h-0.5 bg-gold-500 mb-6" />

              <p className="text-gold-600 font-medium tracking-wide uppercase text-sm mb-4">
                Transparent Pharmacy Pricing
              </p>

              <h1 className="font-display text-5xl md:text-6xl text-navy-800 leading-tight mb-6 tracking-tight">
                Pay What Drugs{' '}
                <span className="text-gold-600">Actually Cost</span>
              </h1>

              <p className="text-xl text-navy-600 mb-8 max-w-lg leading-relaxed">
                No PBM markups. No hidden fees. Just the actual drug cost + 15% + $5 dispensing fee. 
                Designed to save you up to 80% on prescriptions.
              </p>

              {/* Pricing Formula Card */}
              <div className="inline-flex items-center gap-3 px-6 py-4 bg-white/80 backdrop-blur-sm rounded-xl shadow-card border border-navy-100 mb-8">
                <span className="font-mono text-lg">
                  <span className="text-navy-800 font-semibold">Drug Cost</span>
                  <span className="text-navy-400 mx-2">+</span>
                  <span className="text-gold-600 font-semibold">15%</span>
                  <span className="text-navy-400 mx-2">+</span>
                  <span className="text-gold-600 font-semibold">$5</span>
                  <span className="text-navy-400 mx-2">=</span>
                  <span className="text-sage-600 font-bold">Your Price</span>
                </span>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/login">
                  <button className="w-full sm:w-auto px-8 py-4 text-lg font-semibold text-navy-800 bg-gold-500 hover:bg-gold-400 rounded-lg shadow-lg shadow-gold-500/20 transition-all hover:shadow-glow-gold inline-flex items-center justify-center">
                    Get Started <ArrowRight className="ml-2 h-5 w-5" />
                  </button>
                </Link>
                <Link to="/login">
                  <button className="w-full sm:w-auto px-8 py-4 text-lg font-medium text-navy-700 border border-navy-200 hover:bg-navy-50 rounded-lg transition-colors">
                    Log In
                  </button>
                </Link>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-6">
                {[
                  { icon: Shield, text: 'HIPAA-Compliant Protocols' },
                  { icon: Award, text: 'Licensing In Progress' },
                  { icon: Clock, text: 'Fast 2-3 Day Shipping' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-2 text-sm text-navy-600">
                    <item.icon className="h-4 w-4 text-sage-600" />
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column - Price Card */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-elevated border border-navy-100 p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-display text-xl text-navy-800 italic tracking-tight">Example Savings</h3>
                  <span className="px-3 py-1 bg-sage-100 text-sage-700 text-sm font-semibold rounded-full">
                    30-day supply
                  </span>
                </div>

                <div className="space-y-4">
                  {popularDrugs.map((drug) => (
                    <div
                      key={drug.name}
                      className="flex items-center justify-between p-4 bg-navy-50/50 rounded-xl hover:bg-navy-50 transition-colors"
                    >
                      <div>
                        <div className="font-medium text-navy-800">{drug.name}</div>
                        <div className="text-sm text-navy-500">Generic</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-navy-400 line-through">{drug.retailPrice}</div>
                        <div className="text-xl font-bold text-navy-800">{drug.ourPrice}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-sage-50 rounded-xl border border-sage-200">
                  <div className="flex items-center gap-2 text-sage-700 font-medium">
                    <CheckCircle2 className="h-5 w-5 text-sage-600" />
                    <span>Potential savings: up to 80% vs retail</span>
                  </div>
                </div>
                <p className="text-xs text-navy-400 mt-3 text-center">*Estimated pricing based on typical acquisition costs</p>
              </div>

              {/* Floating badge */}
              <div className="absolute -top-3 -right-3 bg-gold-500 text-navy-800 px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                Up to 80%
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gold-500/20 rounded-xl mb-4">
                  <stat.icon className="h-6 w-6 text-gold-400" />
                </div>
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-navy-300">{stat.label}</div>
                {'comingSoon' in stat && stat.comingSoon && (
                  <div className="text-navy-400 text-xs mt-1">(coming soon)</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Telemedicine Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />
            <h2 className="font-display text-3xl md:text-4xl text-navy-800 italic tracking-tight mb-4">
              Need a Prescription?
            </h2>
            <p className="text-lg text-navy-600 max-w-2xl mx-auto">
              Access integrated telemedicine services to consult with licensed providers from home. Get prescriptions for common conditions in hours, not days.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Text Consultation */}
            <div className="bg-sage-50 rounded-2xl p-8 border border-sage-200 hover:shadow-card transition-shadow">
              <div className="w-12 h-12 bg-sage-100 rounded-xl flex items-center justify-center mb-6">
                <MessageCircle className="h-6 w-6 text-sage-600" />
              </div>
              <h3 className="font-display text-xl text-navy-800 italic tracking-tight mb-2">Text Consultation</h3>
              <p className="text-navy-600 mb-4">
                Message a provider about your symptoms. Response within 2 hours.
              </p>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-navy-800">$29</span>
                <Link to="/login">
                  <span className="text-gold-600 font-medium hover:text-gold-700 cursor-pointer">
                    Start now ›
                  </span>
                </Link>
              </div>
            </div>

            {/* Video Consultation */}
            <div className="bg-gold-50 rounded-2xl p-8 border border-gold-200 hover:shadow-card transition-shadow">
              <div className="w-12 h-12 bg-gold-100 rounded-xl flex items-center justify-center mb-6">
                <Video className="h-6 w-6 text-gold-600" />
              </div>
              <h3 className="font-display text-xl text-navy-800 italic tracking-tight mb-2">Video Consultation</h3>
              <p className="text-navy-600 mb-4">
                Face-to-face video call with a licensed provider. Same-day appointments available.
              </p>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-navy-800">$59</span>
                <Link to="/login">
                  <span className="text-gold-600 font-medium hover:text-gold-700 cursor-pointer">
                    Book now ›
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <StepCards
        steps={howItWorksSteps}
        title="Simple, Transparent, Honest"
        subtitle="We believe you deserve to know exactly what you're paying for"
      />

      {/* Final CTA */}
      <section className="py-20 bg-navy-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />
          <h2 className="font-display text-3xl md:text-4xl text-white italic tracking-tight mb-6">
            Ready to Start Saving?
          </h2>
          <p className="text-xl text-navy-200 mb-8">
            Start paying fair prices today. Free to sign up, no insurance required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login">
              <button className="px-8 py-4 text-lg font-semibold text-navy-800 bg-gold-500 hover:bg-gold-400 rounded-lg shadow-lg transition-all hover:shadow-glow-gold">
                Create Free Account
              </button>
            </Link>
            <Link to="/login">
              <button className="px-8 py-4 text-lg font-medium text-white border border-navy-600 hover:bg-navy-700 rounded-lg transition-colors">
                Log In
              </button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

